import { z } from 'zod';
import {
  ACTIVITY_TYPES,
  PRIORITIES,
  WORKFLOW_ACTION_TYPES,
  type WorkflowActionType,
} from '../enums.js';
import { uuidSchema } from '../schemas/common.js';

/**
 * Workflow actions
 * ================
 * Each action type carries its own config shape. They are declared as a
 * discriminated union so an invalid config is rejected when the workflow is
 * saved rather than discovered at 3am when the rule finally fires.
 */

/** Sets a field on the record that triggered the workflow. */
export const updateFieldConfigSchema = z.object({
  field: z.string().min(1).max(120),
  /** A literal, or a merge template such as `Follow up with {{lead.businessName}}`. */
  value: z.unknown(),
});

export const createTaskConfigSchema = z.object({
  subject: z.string().min(1).max(200),
  description: z.string().max(4000).optional(),
  type: z.enum(ACTIVITY_TYPES).default('TASK'),
  priority: z.enum(PRIORITIES).default('NORMAL'),
  /** Days from the moment the workflow fires. */
  dueInDays: z.coerce.number().int().min(0).max(365).default(1),
  /** Omitted, the task goes to whoever owns the record. */
  assigneeId: uuidSchema.optional(),
});

export const sendEmailConfigSchema = z.object({
  templateId: uuidSchema.optional(),
  subject: z.string().min(1).max(200).optional(),
  body: z.string().max(20_000).optional(),
  /** `record` sends to the record's own email; `businessManager` to the record's business manager. */
  to: z.enum(['record', 'businessManager', 'custom']).default('record'),
  toEmail: z.string().max(254).optional(),
});

export const assignBusinessManagerConfigSchema = z.object({
  /** A specific user, or the assignment rule engine picks one. */
  mode: z.enum(['user', 'assignmentRule']).default('user'),
  userId: uuidSchema.optional(),
  ruleId: uuidSchema.optional(),
});

export const notifyUserConfigSchema = z.object({
  /** `businessManager` notifies the record's business manager; `user` a named person; `lineManager` that manager's own manager. */
  target: z.enum(['businessManager', 'user', 'lineManager']).default('businessManager'),
  userId: uuidSchema.optional(),
  title: z.string().min(1).max(200),
  body: z.string().max(1000).optional(),
});

export const requestApprovalConfigSchema = z.object({
  approverId: uuidSchema.optional(),
  reason: z.string().min(1).max(500),
  /** Held until approved, then applied to the record. */
  changes: z.record(z.string(), z.unknown()).default({}),
});

export const workflowActionConfigSchemas = {
  UPDATE_FIELD: updateFieldConfigSchema,
  CREATE_TASK: createTaskConfigSchema,
  SEND_EMAIL: sendEmailConfigSchema,
  ASSIGN_BUSINESS_MANAGER: assignBusinessManagerConfigSchema,
  NOTIFY_USER: notifyUserConfigSchema,
  REQUEST_APPROVAL: requestApprovalConfigSchema,
} as const satisfies Record<WorkflowActionType, z.ZodType>;

export type UpdateFieldConfig = z.infer<typeof updateFieldConfigSchema>;
export type CreateTaskConfig = z.infer<typeof createTaskConfigSchema>;
export type SendEmailConfig = z.infer<typeof sendEmailConfigSchema>;
export type AssignBusinessManagerConfig = z.infer<typeof assignBusinessManagerConfigSchema>;
export type NotifyUserConfig = z.infer<typeof notifyUserConfigSchema>;
export type RequestApprovalConfig = z.infer<typeof requestApprovalConfigSchema>;

/**
 * Validates a config against the schema for its action type.
 *
 * Returns a result rather than throwing, because the caller (the workflow
 * editor, or the engine loading a stored rule) wants to report which action
 * is wrong, not lose the whole workflow to one bad field.
 */
export function parseActionConfig(
  type: WorkflowActionType,
  config: unknown,
): { success: true; data: unknown } | { success: false; error: string } {
  const schema = workflowActionConfigSchemas[type];
  const result = schema.safeParse(config ?? {});
  if (result.success) return { success: true, data: result.data };
  return {
    success: false,
    error: result.error.issues
      .map((issue) => `${issue.path.join('.') || 'config'}: ${issue.message}`)
      .join('; '),
  };
}

export const WORKFLOW_ACTION_LABELS: Record<WorkflowActionType, string> = {
  UPDATE_FIELD: 'Update a field',
  CREATE_TASK: 'Create a task',
  SEND_EMAIL: 'Send an email',
  ASSIGN_BUSINESS_MANAGER: 'Assign a business manager',
  NOTIFY_USER: 'Notify someone',
  REQUEST_APPROVAL: 'Request approval',
};

export const WORKFLOW_ACTION_DESCRIPTIONS: Record<WorkflowActionType, string> = {
  UPDATE_FIELD: 'Writes a value onto the record that triggered the rule.',
  CREATE_TASK: 'Adds a follow-up task, due a set number of days later.',
  SEND_EMAIL: 'Sends a templated email and logs it against the record.',
  ASSIGN_BUSINESS_MANAGER: 'Hands the record to a person, or to an assignment rule.',
  NOTIFY_USER: 'Posts an in-app notification.',
  REQUEST_APPROVAL: 'Holds the change until somebody approves it.',
};

export const ACTION_TYPES = WORKFLOW_ACTION_TYPES;
